import { useEffect, type ReactNode } from "react";
import type { UseFormReturn } from "react-hook-form";
import { Controller as RHFController } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import type { Geofence } from "../../../../../../store/admin/geofencesStore";
import type { ThemeClasses, PointCoordinate } from "../types";
import { LINEAR_BUFFER_DEFAULT } from "../types";
import AddressAutocomplete from "../../../../../../components/base/AddressAutocomplete";
import { CircularCoordinatesForm } from "./forms/CircularCoordinatesForm";
import { LinearCoordinatesForm } from "./forms/LinearCoordinatesForm";
import { PolygonalCoordinatesForm } from "./forms/PolygonalCoordinatesForm";
import { getCustomSelectStyles } from "../../../../shared";
import { getGeofenceTypeOptions } from "../../../../../../utils/getStatusEntity";
import { useStatusesStore } from "../../../../../../store/admin/statusesStore";
import { getStatusOptionsForEntity } from "../../../../../../utils/getStatusEntity";
import { useUsersStore } from "../../../../../../store/admin/usersStore";
import { useAllDevices } from "../../../../../../features/devices/queries";
import { TIMEZONE_OPTIONS } from "../../trips/types";
import { useIsReseller } from "../../../../../../hooks/useIsReseller";

interface GeofenceFormProps {
  isFromTracking?: boolean;
  onBufferChange: (value: number) => void;
  linearAddresses: { address: string | null; geocoding: boolean }[];
  onLinearAddressEdit: (index: number, address: string) => void;
  form: UseFormReturn<Geofence>;
  shape: string;
  isEdit: boolean;
  loadingSubmit: boolean;
  hasRealChanges: () => boolean;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  editGeofence: Geofence | null;
  polygonPoints: PointCoordinate[];
  setPolygonPoints: (points: PointCoordinate[]) => void;
  linearPoints: PointCoordinate[];
  setLinearPoints: (points: PointCoordinate[]) => void;
  defaultCircularCoordinates: {
    latitude: number;
    longitude: number;
    radius: number;
  };
  onAddressSelect: (address: string, lat: number, lng: number) => void;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  // Mapa renderizado por el modal; se coloca en la columna derecha del form.
  mapSlot?: ReactNode;
}

export function GeofenceForm({
  isFromTracking,
  onBufferChange,
  linearAddresses,
  onLinearAddressEdit,
  form,
  shape,
  isEdit,
  loadingSubmit,
  hasRealChanges,
  themeClasses,
  isDarkMode,
  editGeofence,
  polygonPoints,
  setPolygonPoints,
  linearPoints,
  setLinearPoints,
  defaultCircularCoordinates,
  onAddressSelect,
  onSubmit,
  onCancel,
  mapSlot,
}: GeofenceFormProps) {
  const { t } = useTranslation("adminSections");
  const {
    register,
    control,
    watch,
    setValue,
    handleSubmit,
    trigger,
    formState: { errors, isValid },
  } = form;

  // Opciones para selects
  const typeOptions = getGeofenceTypeOptions(t);
  const statuses = useStatusesStore((state) => state.statuses);
  const statusOptions = getStatusOptionsForEntity(statuses, "geofences", t);

  // Vehicles (units), trailers and users data
  const { data: devices = [] } = useAllDevices();
  // Lightweight users (id + name + email only) for the notify select.
  const { usersLight, fetchUsersLight } = useUsersStore();
  const { isReseller } = useIsReseller();

  useEffect(() => {
    // Resellers can notify users from any tenant; bypass the company filter so
    // the dropdown lists every user instead of just the active company.
    fetchUsersLight({ allCompanies: isReseller });
  }, [fetchUsersLight, isReseller]);

  const deviceOptions = devices.map((d) => {
    const vehicleName = d.vehicle ? d.vehicle?.name : "";
    const trailerName = d.remolque ? d.remolque?.name : "";
    const nameDevice = vehicleName || trailerName ? d.identifier : d.name;
    return {
      value: d.id,
      label: `${nameDevice}${vehicleName ? ` (Vehículo: ${vehicleName})` : ""}${trailerName ? ` (Remolque: ${trailerName})` : ""}`,
    };
  });

  const userOptions = usersLight.map((u) => ({
    value: u.id,
    label: `${u.first_name} ${u.last_name} (${u.email})`,
  }));

  const timezoneOptions = Object.entries(TIMEZONE_OPTIONS).map(([value, label]) => ({
    value,
    label,
  }));

  const inputClassName = `w-full text-xs 2xl:text-base px-3 py-2 ${
    isDarkMode ? "bg-dark-card" : "bg-gray-50"
  } border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text}`;

  const originalShape = editGeofence?.shape;
  const isShapeLocked = (target: string) =>
    isEdit && !!originalShape && originalShape !== target;
  const circularLocked = isShapeLocked("circular");
  const polygonalLocked = isShapeLocked("polygonal");
  const linearLocked = isShapeLocked("linear");
  const lockedShapeTitle = t(
    "geofences.form.shapeLockedOnEdit",
    "No se puede cambiar la forma geométrica al editar"
  );

  return (
    <form
      className="flex flex-col w-full gap-4"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
        {/* ═══════ Columna izquierda: datos de la geocerca ═══════ */}
        <div className="flex flex-col gap-4">
      {/* Botones de selección de forma geométrica */}
      <div className="w-full flex flex-col gap-4">
        <label
          className={`block text-sm font-medium ${themeClasses.text} mb-1`}
        >
          {t("geofences.fields.shape", "Forma geométrica")}
        </label>
        <div className="w-full flex items-center justify-center gap-4">
          {!isFromTracking && (
            <button
              type="button"
              onClick={() => { setValue("shape", "circular"); trigger("shape"); }}
              disabled={circularLocked}
              title={circularLocked ? lockedShapeTitle : undefined}
              className={`w-[20%] flex items-center justify-center gap-2 px-4 py-1 rounded-lg font-medium transition-all ${
                shape === "circular"
                  ? "bg-primary-500 text-gray-900"
                  : `${themeClasses.cardBg} ${themeClasses.cardBorder} ${themeClasses.text} ${themeClasses.buttonHover}`
              } ${circularLocked ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <i className="ri-circle-line 2xl:text-lg"></i>
              <span>{t("geofences.shapes.circular", "Circular")}</span>
            </button>
          )}
          {!isFromTracking && (
            <button
              type="button"
              onClick={() => { setValue("shape", "polygonal"); trigger("shape"); }}
              disabled={polygonalLocked}
              title={polygonalLocked ? lockedShapeTitle : undefined}
              className={`w-[20%] flex items-center justify-center gap-2 px-4 py-1 rounded-lg font-medium transition-all ${
                shape === "polygonal"
                  ? "bg-primary-500 text-gray-900"
                  : `${themeClasses.cardBg} ${themeClasses.cardBorder} ${themeClasses.text} ${themeClasses.buttonHover}`
              } ${polygonalLocked ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <i className="ri-pentagon-line 2xl:text-lg"></i>
              <span>{t("geofences.shapes.polygonal", "Poligonal")}</span>
            </button>
          )}
          <button
            type="button"
            onClick={() => { setValue("shape", "linear"); trigger("shape"); }}
            disabled={linearLocked}
            title={linearLocked ? lockedShapeTitle : undefined}
            className={`w-[20%] flex items-center justify-center gap-2 px-4 py-1 rounded-lg font-medium transition-all ${
              shape === "linear"
                ? "bg-primary-500 text-gray-900"
                : `${themeClasses.cardBg} ${themeClasses.cardBorder} ${themeClasses.text} ${themeClasses.buttonHover}`
            } ${linearLocked ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <i className="ri-route-line 2xl:text-lg"></i>
            <span>{t("geofences.shapes.linear", "Lineal")}</span>
          </button>
        </div>
        <input type="hidden" {...register("shape", { required: true })} />
        {errors.shape && (
          <span className="text-red-500 text-xs">
            {t("geofences.form.selectShapeRequired", "Debe seleccionar una forma geométrica")}
          </span>
        )}
      </div>

      {/* Línea divisoria */}
      <hr className={`w-full ${themeClasses.border} my-2`} />

      {/* Nombre */}
      <div className="flex flex-col gap-2">
        <label
          className={`block text-sm font-medium ${themeClasses.text} mb-1`}
        >
          {t("geofences.fields.name", "Nombre de la geocerca")} <span className="text-red-500">*</span>
        </label>
        <input
          {...register("name", { required: true })}
          name="name"
          type="text"
          className={`${inputClassName} ${
            errors.name
              ? "focus:outline-none ring-2 ring-red-500"
              : "focus:outline-none focus:ring-2 focus:ring-primary-500"
          }`}
          placeholder={t("geofences.form.namePlaceholder", "Ej: Centro de Distribución Norte")}
        />
        {errors.name && (
          <span className="text-red-500 text-xs ml-3">
            {t("geofences.form.requiredField", "Este campo es obligatorio")}
          </span>
        )}
      </div>

      {/* Dirección — en forma lineal la reemplazan las direcciones por extremo,
          y `address` se sincroniza con la del punto inicial. */}
      <div className={`flex flex-col gap-2 ${shape === "linear" ? "hidden" : ""}`}>
        <label
          className={`block text-sm font-medium ${themeClasses.text} mb-1`}
        >
          {t("geofences.fields.address", "Dirección")} <span className="text-red-500">*</span>
        </label>
        <AddressAutocomplete
          key={`address-${shape}`}
          onAddressSelect={onAddressSelect}
          onAddressTextChange={(text) => {
            setValue("address", text, { shouldValidate: true, shouldDirty: true });
          }}
          placeholder={t("geofences.form.addressPlaceholder", "Ej: Calle Principal #123, Ciudad, País")}
          defaultValue={editGeofence?.address || ""}
          // Mantener el input controlado por el estado del form para que el
          // reverse-geocoder de `GeofenceMapPreview` (que actualiza
          // `address` cuando arrastras el marcador o cambias de forma
          // circular/poligonal/lineal) se refleje visualmente. Antes solo
          // se pasaba el value en modo tracking y, al cambiar el "cursor"
          // del mapa, el form quedaba con la dirección nueva pero el
          // input mostraba la anterior (o vacío tras el remount por key).
          value={watch("address") ?? ""}
          className={`${inputClassName} focus:outline-none focus:ring-2 focus:ring-primary-500`}
        />
        <input {...register("address", { required: true })} type="hidden" />
        {errors.address && (
          <span className="text-red-500 text-xs ml-3">
            {t("geofences.form.requiredField", "Este campo es obligatorio")}
          </span>
        )}
      </div>

      {/* Tipo y Estado */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-2">
          <label
            className={`block text-sm font-medium ${themeClasses.text} mb-1`}
          >
            {t("geofences.fields.type", "Tipo")} <span className="text-red-500">*</span>
          </label>
          <RHFController
            name="type"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Select
                {...field}
                options={typeOptions}
                styles={getCustomSelectStyles(isDarkMode)}
                placeholder={t("geofences.form.selectType", "Seleccionar tipo")}
                value={typeOptions.find((opt) => opt.value === field.value)}
                onChange={(option: any) => field.onChange(option?.value)}
                isSearchable
                noOptionsMessage={() => t("geofences.form.noTypesAvailable", "Sin tipos disponibles")}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            )}
          />
          {errors.type && (
            <span className="text-red-500 text-xs">
              {t("geofences.form.requiredField", "Campo obligatorio")}
            </span>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <label
            className={`block text-sm font-medium ${themeClasses.text} mb-1`}
          >
            {t("geofences.fields.status", "Estado")} <span className="text-red-500">*</span>
          </label>
          <RHFController
            name="status_id"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Select
                {...field}
                options={statusOptions}
                styles={getCustomSelectStyles(isDarkMode)}
                placeholder={t("geofences.form.selectStatus", "Seleccionar estado")}
                value={statusOptions.find((opt: any) => opt.value === field.value)}
                onChange={(option: any) => field.onChange(option?.value)}
                isSearchable
                noOptionsMessage={() => t("geofences.form.noStatusAvailable", "Sin estados disponibles")}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            )}
          />
          {errors.status_id && (
            <span className="text-red-500 text-xs">
              {t("geofences.form.requiredField", "Campo obligatorio")}
            </span>
          )}
        </div>
      </div>

      {/* Descripción */}
      <div className="flex flex-col gap-2">
        <label
          className={`block text-sm font-medium ${themeClasses.text} mb-1`}
        >
          {t("geofences.fields.description", "Descripción")} <span className="text-red-500">*</span>
        </label>
        <textarea
          {...register("description", { required: true })}
          className={`${inputClassName} ${
            errors.description
              ? "focus:outline-none ring-2 ring-red-500"
              : "focus:outline-none focus:ring-2 focus:ring-primary-500"
          } resize-none`}
          rows={3}
          placeholder={t("geofences.form.descriptionPlaceholder", "Descripción de la geocerca y su propósito...")}
        />
        {errors.description && (
          <span className="text-red-500 text-xs ml-3">
            {t("geofences.form.requiredField", "Campo obligatorio")}
          </span>
        )}
      </div>

      {/* Disparadores: entrada / salida (geofenceActions) */}
      <div className="flex flex-col gap-2">
        <label
          className={`block text-sm font-medium ${themeClasses.text} mb-1`}
        >
          {t("geofences.fields.crossingTriggers", "Eventos al cruzar")}
        </label>
        <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"} -mt-1 mb-1`}>
          {t(
            "geofences.form.crossingTriggersHint",
            "Indica si quieres reaccionar al entrar, al salir o en ambos casos."
          )}
        </p>
        <RHFController
          name="geofenceActions"
          control={control}
          render={({ field }) => {
            const value = (field.value as ("entry" | "exit")[] | undefined) ?? [];
            const toggle = (key: "entry" | "exit") =>
              field.onChange(
                value.includes(key) ? value.filter((a) => a !== key) : [...value, key]
              );
            const CROSSING_ACTIONS = [
              {
                key: "entry",
                icon: "ri-login-circle-line",
                label:
                  shape === "linear"
                    ? t("geofences.fields.entryCorridor", "Entrada al corredor")
                    : t("geofences.fields.entry", "Entrada"),
              },
              {
                key: "exit",
                icon: "ri-logout-circle-line",
                label:
                  shape === "linear"
                    ? t("geofences.fields.exitCorridor", "Salida del corredor")
                    : t("geofences.fields.exit", "Salida"),
              },
            ] as const;
            return (
              <div className="flex flex-wrap gap-3">
                {CROSSING_ACTIONS.map(({ key, icon, label }) => {
                  const active = value.includes(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => toggle(key)}
                      className={`flex-1 min-w-[140px] flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-all border shadow-sm ${
                        active
                          ? "bg-primary-500 border-primary-400 text-gray-900 shadow-md ring-2 ring-primary-400/40"
                          : `${themeClasses.cardBg} ${themeClasses.cardBorder} ${themeClasses.text} ${themeClasses.buttonHover} opacity-90`
                      }`}
                    >
                      <span
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-lg ${
                          active ? "bg-gray-900/10" : isDarkMode ? "bg-white/5" : "bg-gray-100"
                        }`}
                      >
                        <i className={icon} aria-hidden />
                      </span>
                      <span className="text-left leading-tight">
                        {label}
                        <span
                          className={`block text-[10px] font-normal uppercase tracking-wide ${
                            active ? "text-gray-800/80" : "text-gray-500"
                          }`}
                        >
                          {key}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          }}
        />
      </div>

      {/* Dispositivos asignados */}
      <div className="flex flex-col gap-2">
        <label
          className={`block text-sm font-medium ${themeClasses.text} mb-1`}
        >
          {t("geofences.fields.devices", "Dispositivos asignados")}
        </label>
        <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"} -mt-1 mb-1`}>
          {t("geofences.form.devicesHint", "Deja vacío para aplicar a todos los dispositivos de la empresa")}
        </p>
        <RHFController
          name="devices"
          control={control}
          render={({ field }) => (
            <Select
              isMulti
              options={deviceOptions}
              styles={getCustomSelectStyles(isDarkMode)}
              placeholder={t("geofences.form.selectDevices", "Seleccionar dispositivos...")}
              value={deviceOptions.filter((opt) =>
                (field.value as string[] | undefined)?.includes(opt.value)
              )}
              onChange={(selected) =>
                field.onChange(
                  selected
                    ? (selected as { value: string }[]).map((s) => s.value)
                    : []
                )
              }
              isClearable
              isSearchable
              noOptionsMessage={() =>
                t("geofences.form.noDevicesAvailable", "Sin dispositivos disponibles")
              }
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          )}
        />
      </div>

      {/* Usuarios notificados */}
      <div className="flex flex-col gap-2">
        <label
          className={`block text-sm font-medium ${themeClasses.text} mb-1`}
        >
          {t("geofences.fields.notifiedUsers", "Usuarios a notificar")}
        </label>
        <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"} -mt-1 mb-1`}>
          {t("geofences.form.notifiedUsersHint", "Selecciona usuarios existentes")}
        </p>
        <RHFController
          name="notifiedUsers"
          control={control}
          render={({ field }) => {
            const currentValues = (field.value as string[] | undefined) || [];
            const selectedOptions = currentValues.map((val) => {
              const existing = userOptions.find((o) => o.value === val);
              return existing || { value: val, label: val };
            });
            return (
              <Select
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                options={userOptions}
                styles={getCustomSelectStyles(isDarkMode)}
                placeholder={t(
                  "geofences.form.selectUsers",
                  "Seleccionar usuarios..."
                )}
                value={selectedOptions}
                onChange={(selected) =>
                  field.onChange(
                    selected
                      ? (selected as { value: string }[]).map((s) => s.value)
                      : []
                  )
                }
                isClearable
                isSearchable
                noOptionsMessage={() =>
                  t(
                    "geofences.form.noUsersAvailable",
                    "Sin usuarios disponibles"
                  )
                }
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            );
          }}
        />
      </div>

      {/* Envío de correos además del push */}
      <div className="flex flex-col gap-2">
        <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>
          {t("geofences.fields.sendEmails")}
        </label>
        <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"} -mt-1 mb-1`}>
          {t("geofences.form.sendEmailsHint")}
        </p>
        <RHFController
          name="sendEmails"
          control={control}
          render={({ field }) => {
            const active = field.value === true;
            return (
              <button
                type="button"
                role="switch"
                aria-checked={active}
                onClick={() => field.onChange(!active)}
                className={`flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                  active
                    ? "bg-primary-500/10 border-primary-400 text-primary-600"
                    : `${themeClasses.cardBg} ${themeClasses.cardBorder} ${themeClasses.text} ${themeClasses.buttonHover}`
                }`}
              >
                <span className="flex items-center gap-2">
                  <i className="ri-mail-send-line 2xl:text-lg" aria-hidden />
                  {active
                    ? t("geofences.form.sendEmailsOn")
                    : t("geofences.form.sendEmailsOff")}
                </span>
                <span
                  className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                    active ? "bg-primary-500" : "bg-gray-400/50"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      active ? "translate-x-4" : "translate-x-0.5"
                    }`}
                  />
                </span>
              </button>
            );
          }}
        />
      </div>
        </div>

        {/* ═══════ Columna derecha: mapa + zona horaria/coordenadas ═══════ */}
        <div className="flex flex-col gap-4">
          {/* MAPA */}
          {mapSlot && (
            <div className="flex-1 min-h-[340px] rounded-xl overflow-hidden">
              {mapSlot}
            </div>
          )}

      {/* Zona horaria para notificaciones */}
      <div className="flex flex-col gap-2">
        <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>
          {t("geofences.fields.timezone", "Zona horaria")}
        </label>
        <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"} -mt-1 mb-1`}>
          <i className="ri-time-zone-line mr-1" />
          {t(
            "geofences.form.timezoneHint",
            "Esta zona horaria se usará para las notificaciones enviadas a los usuarios."
          )}
        </p>
        <RHFController
          name="timezone"
          control={control}
          render={({ field }) => (
            <Select
              options={timezoneOptions}
              styles={getCustomSelectStyles(isDarkMode)}
              placeholder={t("geofences.form.selectTimezone", "Seleccionar zona horaria")}
              value={
                timezoneOptions.find((opt) => opt.value === field.value) ||
                timezoneOptions.find((opt) => opt.value === "America/Mexico_City")
              }
              onChange={(option: any) =>
                field.onChange(option?.value || "America/Mexico_City")
              }
              isSearchable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          )}
        />
      </div>

      {/* Inputs dinámicos según la forma */}
      {shape === "circular" && (
        <CircularCoordinatesForm
          control={control}
          register={register}
          watch={watch}
          trigger={trigger}
          errors={errors}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          defaultLatitude={defaultCircularCoordinates.latitude}
          defaultLongitude={defaultCircularCoordinates.longitude}
        />
      )}

      {shape === "linear" && (
        <LinearCoordinatesForm
          linearPoints={linearPoints}
          setLinearPoints={setLinearPoints}
          setValue={setValue}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          linearAddresses={linearAddresses}
          onAddressEdit={onLinearAddressEdit}
          buffer={Number(watch("buffer")) || LINEAR_BUFFER_DEFAULT}
          onBufferChange={onBufferChange}
        />
      )}

      {shape === "polygonal" && (
        <PolygonalCoordinatesForm
          polygonPoints={polygonPoints}
          setPolygonPoints={setPolygonPoints}
          setValue={setValue}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
        />
      )}

        </div>
      </div>

      {/* Botones */}
      <div className={`flex space-x-3 pt-4 mt-2 border-t ${themeClasses.border}`}>
        <button
          type="button"
          onClick={onCancel}
          className={`flex-1 text-xs 2xl:text-base py-2 px-4 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} font-medium transition-colors`}
        >
          {t("geofences.actions.cancel", "Cancelar")}
        </button>
        <button
          type="submit"
          className={`flex-1 text-xs 2xl:text-base py-2 px-4 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg font-medium transition-colors ${
            !isValid || (isEdit && !hasRealChanges()) || loadingSubmit
              ? "opacity-50 cursor-not-allowed"
              : ""
          }`}
          disabled={!isValid || (isEdit && !hasRealChanges()) || loadingSubmit}
        >
          {loadingSubmit
            ? isEdit
              ? t("geofences.form.submitting", "Guardando...")
              : t("geofences.form.creating", "Creando...")
            : isEdit
            ? t("geofences.actions.saveChanges", "Guardar Cambios")
            : t("geofences.actions.create", "Crear Geocerca")}
        </button>
      </div>
    </form>
  );
}
